import { useState } from 'react'
import {
	parseRowsPerPage,
	type RowsPerPageValue
} from '@/components/ui/rows-per-page-select'
import { DEFAULT_PAGE_LIMIT } from '@/helpers/constants'

interface UseRowsPerPageParams {
	/** The raw `perPage` search param, straight off the URL. */
	searchParam: string | undefined
	/** Writes the param back; `undefined` drops it from the URL. */
	setSearchParam: (perPage: string | undefined) => void
}

/**
 * Keeps the rows-per-page choice in the URL, next to `page`, so a size survives
 * a reload and travels with a shared link the same way a filter does.
 *
 * The size doubles as the `perPage` the list endpoints take; feed the response's
 * total back through `reportTotal` for the "1-50 of 128" label.
 */
export const useRowsPerPage = ({
	searchParam,
	setSearchParam
}: UseRowsPerPageParams) => {
	const rowsPerPage = parseRowsPerPage(searchParam, DEFAULT_PAGE_LIMIT)

	// Holds the last total we were told about, so the row count stays put
	// through the loading gap between pages instead of blinking out on every
	// refetch.
	const [totalRows, setTotalRows] = useState<number>()

	const setRowsPerPage = (newRowsPerPage: RowsPerPageValue) => {
		setSearchParam(
			// The default stays out of the URL, so the common case has a clean
			// link and the default can change later without stale params
			// pinning old pages to the old size.
			newRowsPerPage === DEFAULT_PAGE_LIMIT
				? undefined
				: String(newRowsPerPage)
		)
	}

	return {
		// The chosen size is the `perPage` the endpoints take, verbatim.
		rowsPerPage,
		setRowsPerPage,
		totalRows,
		/**
		 * Feeds the hook the total from the latest response. Call it during
		 * render, right after the query: the no-op guard makes the repeat calls
		 * free, and the one call that does change something triggers a
		 * render-phase update, which React re-runs before painting.
		 */
		reportTotal: (total: number | undefined) => {
			if (total !== undefined && total !== totalRows) {
				setTotalRows(total)
			}
		}
	}
}
